import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Chip from '@/components/Common/Chip';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {colors, fontSize, fontWeight, borderRadius, borderWidth, spacing} from '@/styles/tokens';

// ─── Module-level sx constants ────────────────────────────────────────────────
// Active-filter chip look — component-local inline style (was PortfolioFilters.module.scss).
// Layered over the Chip primitive's `.chip-root` (transparent, border:0); inline beats the
// base class so no `!important` is needed. border-radius is omitted because the primitive
// default (--chip-radius = borderRadius.xl = 16px) already equals MUI's prior 16px.
const portfolioChipStyle: React.CSSProperties = {
    backgroundColor: colors.primary.deep, // #1F3F7A
    color: colors.text.inverse, // #ffffff — label + remove icon (inherits via currentColor)
    border: `1px solid ${colors.primary.deep}`,
    paddingLeft: 8, // MUI sx px:1 = theme.spacing(1) = 8px
    paddingRight: 8,
    fontSize: fontSize[15], // 0.9375rem = 15px
    fontWeight: fontWeight.medium // 500
};

const chipButtonStylesSx = {
    backgroundColor: colors.background.default,
    color: colors.text.primary,
    border: `1px solid ${colors.text.primary}`,
    borderRadius: borderRadius.xl,
    textTransform: 'none',
    px: 1,
    fontSize: fontSize[15],
    fontWeight: fontWeight.medium,
    height: '32px',
    minHeight: '32px',
    lineHeight: 1,
    '& .MuiButton-endIcon': {marginLeft: '6px'}
} as const;

const filterRowSx = {flexDirection: {xs: 'column', sm: 'row'}, alignItems: {xs: 'stretch', sm: 'center'}} as const;
const formControlSx = {width: {xs: '100%', sm: 'auto'}} as const;
// Was `.filterBox` in globals.scss's `portfolio` partial (exclusive to this
// component); folded into the pre-existing responsive-width sx below.
const filterBoxSx = {
    width: {xs: '100%', sm: 'auto'},
    border: `${borderWidth.thin} solid ${colors.text.primary}`,
    borderRadius: borderRadius.sm,
    paddingTop: spacing[8],
    paddingRight: '10px', // no exact spacing-token match at 10px
    paddingBottom: spacing[8],
    paddingLeft: spacing[16],
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    minWidth: '120px', // no exact spacing-token match at 120px
    gap: '100px', // no exact spacing-token match at 100px
    backgroundColor: colors.background.default
} as const;
const flexGrowSx = {flexGrow: 1} as const;
const arrowRotateSx = {transform: 'rotate(180deg)', transition: 'transform 0.2s'} as const;
const arrowNormalSx = {transform: 'rotate(0deg)', transition: 'transform 0.2s'} as const;
const filterCloseSx = {position: 'absolute', right: 12, top: 12} as const;
const checkboxSx = {color: colors.text.primary, '&.Mui-checked': {color: colors.primary.main}} as const;
const closeIconSmSx = {fontSize: 16} as const;
// ─────────────────────────────────────────────────────────────────────────────

const INDUSTRIES = [
    'Healthcare',
    'Edtech',
    'LegalTech',
    'FinTech',
    'Retail',
    'Recruitment',
    'Supply Chain & Logistics',
    'Media & Entertainment',
    'AgriTech',
    'Energy & Natural Resources',
    'Enterprise Technology Solutions',
    'Real Estate',
    'Hospitality',
    'Fitness & Wellness',
    'Insurance & InsurTech',
    'Food & Restaurant'
];

const SERVICES = [
    'Artificial Intelligence',
    'Web Development',
    'Mobile Development',
    'Blockchain Development',
    'Cryptocurrency Development',
    'Enterprise SaaS'
];

interface PortfolioFiltersProps {
    openFilter: null | 'industry' | 'service';
    selectedFilters: string[];
    filteredCount: number;
    variant: 'portfolio' | 'homepage';
    onToggleFilter: (type: 'industry' | 'service') => void;
    onFilterChange: (value: string) => void;
    onRemoveFilter: (value: string) => void;
    onClearAll: () => void;
}

const PortfolioFilters = ({
    openFilter,
    selectedFilters,
    filteredCount,
    variant,
    onToggleFilter,
    onFilterChange,
    onRemoveFilter,
    onClearAll
}: PortfolioFiltersProps) => {
    const filterMenuContent = openFilter ? (
        <Box
            border={`1px solid ${colors.neutral.borderSoft}`}
            borderRadius={1}
            padding="16px 20px"
            mt={2}
            position="relative"
            sx={{
                width: {xs: '100%', md: 'auto'},
                backgroundColor: variant === 'homepage' ? colors.background.default : colors.background.subtle
            }}
        >
            <IconButton size="small" sx={filterCloseSx} onClick={() => onToggleFilter(openFilter)}>
                <CloseIcon />
            </IconButton>
            <Grid container spacing={0}>
                {(openFilter === 'industry' ? INDUSTRIES : SERVICES)?.map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedFilters.includes(item)}
                                    onChange={() => onFilterChange(item)}
                                    sx={checkboxSx}
                                />
                            }
                            label={item}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    ) : null;

    return (
        <Box mb={4}>
            {/* Filter dropdowns */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent={variant === 'homepage' ? 'center' : 'flex-start'}
                gap={2}
                sx={filterRowSx}
            >
                <Typography className="font-headers" style={{fontSize: fontSize[22]}}>
                    Filter By:
                </Typography>

                <FormControl size="small" sx={formControlSx}>
                    <Box sx={filterBoxSx} onClick={() => onToggleFilter('industry')}>
                        <Typography sx={flexGrowSx}>Industries</Typography>
                        <KeyboardArrowDownIcon sx={openFilter === 'industry' ? arrowRotateSx : arrowNormalSx} />
                    </Box>
                </FormControl>

                {/* Mobile: Industry dropdown inline */}
                <Box sx={{display: {xs: openFilter === 'industry' ? 'block' : 'none', sm: 'none'}, width: '100%'}}>{filterMenuContent}</Box>

                <FormControl size="small" sx={formControlSx}>
                    <Box sx={filterBoxSx} onClick={() => onToggleFilter('service')}>
                        <Typography sx={flexGrowSx}>Services</Typography>
                        <KeyboardArrowDownIcon sx={openFilter === 'service' ? arrowRotateSx : arrowNormalSx} />
                    </Box>
                </FormControl>

                {/* Mobile: Service dropdown inline */}
                <Box sx={{display: {xs: openFilter === 'service' ? 'block' : 'none', sm: 'none'}, width: '100%'}}>{filterMenuContent}</Box>
            </Box>

            {/* Desktop dropdown panel */}
            <Box sx={{display: {xs: 'none', sm: 'block'}}}>{filterMenuContent}</Box>

            {/* Active filter chips */}
            {selectedFilters.length > 0 && (
                <Box mt={3} mb={3} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <Typography fontWeight={600}>{filteredCount} results for :</Typography>
                    {selectedFilters?.map((filter) => (
                        <Chip
                            key={filter}
                            style={portfolioChipStyle}
                            onRemove={() => onRemoveFilter(filter)}
                            removeLabel={`Remove ${filter}`}
                        >
                            {filter}
                        </Chip>
                    ))}
                    <Button size="small" endIcon={<CloseIcon sx={closeIconSmSx} />} onClick={onClearAll} sx={chipButtonStylesSx}>
                        Clear all
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default memo(PortfolioFilters);

// Export lists for use in parent
export {INDUSTRIES, SERVICES};
